import React, { memo, useContext, useEffect } from 'react';

import type { InlineCommentViewComponentProps } from '@atlaskit/editor-common/types';

import {
	useCommentsContentState,
	useCommentsContentActions,
} from '@confluence/comment-context/entry-points/useCommentsContent.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import { CommentWarningDialog } from '@confluence/comment-dialogs';
import {
	useAnnotations,
	useAnnotationsDispatch,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import {
	ADD_INLINE_COMMENT_PUBLISH_EXPERIENCE,
	VIEW_INLINE_COMMENT_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { getCommentsDataManagerExperimentAttributes } from '@confluence/experience-tracker/entry-points/getCommentsDataManagerExperimentAttributes.ts';
import { fg } from '@confluence/feature-gating';

import type { ViewComponentProps } from './ViewComponent.tsx';

type ViewManagerProps = ViewComponentProps & {
	setViewProps: (viewProps: (InlineCommentViewComponentProps & ViewComponentProps) | null) => void;
	getReactionMarkerMap?: () => Record<string, string>;
};

export const ViewManagerWithProps = ({
	pageId,
	updateDocument,
	setViewProps,
	isArchived,
	annotations,
	clickElementTarget,
	deleteAnnotation,
	getInlineNodeTypes,
	getReactionMarkerMap,
}: ViewManagerProps & InlineCommentViewComponentProps) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { hasContentChanged } = useCommentsContentState();
	const { resetContentChanged } = useCommentsContentActions();
	const { showModal } = useDialogs();
	const reactionMarkerMap = getReactionMarkerMap?.();
	const { newlyCreatedAnnotationId, storedCommentData } = useAnnotations();
	const {
		handleClose: handleSelectionClose,
		setNewlyCreatedAnnotationId,
		clearStoredCommentData,
	} = useAnnotationsDispatch();

	// Clear stored comment data when annotation changes (before new component renders)
	useEffect(() => {
		if (storedCommentData) {
			const currentAnnotationId = annotations?.[0]?.id;
			const storedAnnotationId = storedCommentData?.location?.inlineMarkerRef;

			// Clear stored data if we're viewing a different annotation
			if (currentAnnotationId && storedAnnotationId && currentAnnotationId !== storedAnnotationId) {
				clearStoredCommentData();
			}
		}
	}, [annotations, storedCommentData, clearStoredCommentData]);

	useEffect(() => {
		const wasHighlightClicked = Boolean(annotations.length && clickElementTarget);

		const handleSetViewProps = () => {
			// Below logic has been added to prevent the nested renderers like
			// excerpt/page properties macros to not set the wrong value of isArchived.
			// Thus, we need to setViewProps only when the "PageStatusQuery" defined in ViewPageComponent resolves

			if (isArchived !== undefined) {
				setViewProps({
					pageId,
					updateDocument,
					isArchived,
					annotations,
					clickElementTarget,
					deleteAnnotation,
					wasHighlightClicked,
					getInlineNodeTypes,
					reactionMarkerMap,
				});
			}

			// If we're showing this coming from a newly created comment, clear the props and succeed the experience
			if (annotations.length && annotations[0].id === newlyCreatedAnnotationId) {
				setNewlyCreatedAnnotationId(undefined);
				experienceTracker.succeed({
					name: ADD_INLINE_COMMENT_PUBLISH_EXPERIENCE,
				});
			}
		};

		// the if will be replaced with the new preemptive gate next/packages/annotation-provider-inline-comments/src/hooks/usePreemptiveGate.ts when cleanup platform_editor_comments_api_manager
		if (hasContentChanged && wasHighlightClicked && !fg('platform_editor_comments_api_manager')) {
			let abortedKeyboardExperience = false;

			const experienceState = experienceTracker.getExperienceState(VIEW_INLINE_COMMENT_EXPERIENCE);

			/*
			 * If a keyboard shortcut is used to navigate when the content has changed, we abort and restart
			 * the experience on confirm because we can't predict if the user will confirm the navigation, or
			 * how long the user will take to confirm.
			 */
			if (
				experienceState !== null &&
				experienceState?.hasStopped === false &&
				experienceState?.attributes?.entryPoint === 'comments-shortcuts/ViewCommentShortcutListener'
			) {
				experienceTracker.abort({
					name: VIEW_INLINE_COMMENT_EXPERIENCE,
					reason: 'Comment warning dialog displayed',
					checkForTimeout: true,
					publishDebugPoints: true,
					attributes: {
						stopPoint: 'annotation-provider-inline-comments/renderer/ViewManager',
					},
				});

				abortedKeyboardExperience = true;
			}

			showModal(CommentWarningDialog, {
				onConfirm: () => {
					if (abortedKeyboardExperience) {
						experienceTracker.start({
							name: VIEW_INLINE_COMMENT_EXPERIENCE,
							timeout: ExperienceTimeout.VIEW_INLINE_COMMENT,
							attributes: {
								...getCommentsDataManagerExperimentAttributes(),
								annotationId: annotations.length && annotations[0].id,
								entryPoint: 'annotation-provider-inline-comments/renderer/ViewManager',
								pageClass: 'renderer',
							},
						});

						experienceTracker.attachDebugPoint({
							name: VIEW_INLINE_COMMENT_EXPERIENCE,
							message:
								'Restarting experience after comment was discarded from comment warning dialog. Initial entrypoint: comments-shortcuts/ViewCommentShortcutListener',
						});
					}

					resetContentChanged();
					handleSelectionClose();
					handleSetViewProps();
				},
			});
		} else {
			handleSetViewProps();
		}

		// We only want to listen to changes that signifies a new comment click
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [annotations, clickElementTarget]);

	return null;
};

export const ViewManager = ({
	pageId,
	updateDocument,
	setViewProps,
	isArchived,
	getReactionMarkerMap,
}: ViewManagerProps) =>
	memo((viewComponentProps: InlineCommentViewComponentProps) => (
		<ViewManagerWithProps
			pageId={pageId}
			updateDocument={updateDocument}
			setViewProps={setViewProps}
			isArchived={isArchived}
			getReactionMarkerMap={getReactionMarkerMap}
			{...viewComponentProps}
		/>
	));
